/* ProductVariantCard - Compact responsive product variant row matching reference design.
 * Mobile: horizontal card with image left, info right, price+actions row at bottom.
 * Desktop: wide row with image, info+price tiers, price+quantity+cart column.
 * Updated: Kluit/Pot type badges now use transparent background with green border and black text.
 * Note: Prices received from parent are already including BTW, no need to recalculate.
 * Updated: Added DataFast add_to_cart goal.
 * Updated: Staffelprijs badges now show green badge for cheapest tier, added encouragement text,
 *          right column always shows cheapest price, and first tier range replaces next-tier hint.
 * Updated: Mobile now shows cheapest tier price with "VANAF" label,
 *          "Elders goedkoper?" underneath the "Toevoegen" button.
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ShoppingCart, Plus, Minus, CalendarClock, Heart, Package, Tag } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { isAvailableFromFuture, formatDutchDate } from '@/lib/utils';

import { useCart } from '@/contexts/CartContext';
import { useFavorites } from '@/contexts/FavoritesContext';
import { toast } from 'sonner';
import PriceMatchModal from '@/components/product/PriceMatchModal';
import { trackGoal } from '@/lib/datafast';

interface ProductVariant {
  id: string;
  size: string;
  type: string;
  price_tier_1: string;
  price_tier_2?: string | null;
  price_tier_3?: string | null;
  price_tier_4?: string | null;
  minimum_order_quantity: number;
  stock_quantity: number;
  sku: string;
  slug: string;
  images: string[];
  variantImages: string[];
  genericImages: string[];
  price?: string;
  supplier?: string;
  available_from?: string | null;
}

interface ProductVariantCardProps {
  variant: ProductVariant;
  productName: string;
  productSlug?: string;
  categorySlug?: string;
}

export default function ProductVariantCard({ variant, productName, productSlug, categorySlug }: ProductVariantCardProps) {
  const [isHovered, setIsHovered] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const [priceMatchOpen, setPriceMatchOpen] = useState(false);
  const [quantity, setQuantity] = useState(variant.minimum_order_quantity || 25);
  const [inputValue, setInputValue] = useState((variant.minimum_order_quantity || 25).toString());
  const { addItem } = useCart();
  const { isFavorite, toggleFavorite } = useFavorites();
  const navigate = useNavigate();
  const favoriteId = variant.id;

  const basePriceTier1 = parseFloat(variant.price_tier_1 || variant.price || '0');
  const priceTier2 = variant.price_tier_2 ? parseFloat(variant.price_tier_2) : null;
  const priceTier3 = variant.price_tier_3 ? parseFloat(variant.price_tier_3) : null;
  const priceTier4 = variant.price_tier_4 ? parseFloat(variant.price_tier_4) : null;

  const getCurrentPrice = (qty: number) => {
    const isKaleWortel = variant.type === 'Kale wortel';
    if (isKaleWortel) {
      if (priceTier4 && qty >= 500) return priceTier4;
      if (priceTier3 && qty >= 300) return priceTier3;
      if (priceTier2 && qty >= 200) return priceTier2;
      return basePriceTier1;
    } else {
      if (priceTier4 && qty >= 101) return priceTier4;
      if (priceTier3 && qty >= 76) return priceTier3;
      if (priceTier2 && qty >= 51) return priceTier2;
      return basePriceTier1;
    }
  };

  const getNextTierInfo = (qty: number) => {
    const isKaleWortel = variant.type === 'Kale wortel';
    if (isKaleWortel) {
      if (qty < 100 && basePriceTier1) return { threshold: 100, price: basePriceTier1 };
      if (qty >= 100 && qty < 200 && priceTier2) return { threshold: 200, price: priceTier2 };
      if (qty >= 200 && qty < 300 && priceTier3) return { threshold: 300, price: priceTier3 };
      if (qty >= 300 && qty < 500 && priceTier4) return { threshold: 500, price: priceTier4 };
    } else {
      if (qty < 51 && priceTier2) return { threshold: 51, price: priceTier2 };
      if (qty >= 51 && qty < 76 && priceTier3) return { threshold: 76, price: priceTier3 };
      if (qty >= 76 && qty < 101 && priceTier4) return { threshold: 101, price: priceTier4 };
    }
    return null;
  };

  const currentPrice = getCurrentPrice(quantity);
  const formattedPrice = currentPrice > 0 ? `€${currentPrice.toFixed(2)}` : 'Prijs op aanvraag';
  const hasGraduatedPricing = priceTier2 || priceTier3 || priceTier4;
  const isInStock = variant.stock_quantity > 0;
  const nextTier = getNextTierInfo(quantity);
  const isNotYetAvailable = isAvailableFromFuture(variant.available_from);

  const variantUrl = variant.slug ? `/${variant.slug}` : '#';

  const displayImage = isHovered && variant.genericImages.length > 0
    ? variant.genericImages[0]
    : (variant.variantImages.length > 0 ? variant.variantImages[0] : variant.genericImages[0]);

  const handleAddToCart = async () => {
    const finalQuantity = parseInt(inputValue) || variant.minimum_order_quantity || 25;
    if (finalQuantity < variant.minimum_order_quantity) {
      toast.error(`Minimale bestelhoeveelheid is ${variant.minimum_order_quantity} stuks`);
      setInputValue(variant.minimum_order_quantity.toString());
      setQuantity(variant.minimum_order_quantity);
      return;
    }
    const validQuantity = Math.min(999, finalQuantity);
    setQuantity(validQuantity);
    setInputValue(validQuantity.toString());
    setIsAdding(true);
    try {
      await addItem({
        product_variant_id: variant.id,
        product_name: productName,
        variant_size: variant.size,
        variant_type: variant.type as 'Kluit' | 'Pot' | 'Kale wortel',
        unit_price: getCurrentPrice(validQuantity),
        image_url: displayImage,
        slug: productSlug,
        quantity: validQuantity,
      });
      const minQty = variant.minimum_order_quantity || 25;
      setQuantity(minQty);
      setInputValue(minQty.toString());
      trackGoal('add_to_cart', { product_name: productName, variant_size: variant.size, quantity: validQuantity });
    } catch (error) {
      console.error('Error adding to cart:', error);
      toast.error('Er ging iets mis bij het toevoegen aan de winkelwagen');
    } finally {
      setIsAdding(false);
    }
  };

  const getWhatsAppLink = () => {
    const phoneNumber = '31850233564';
    const message = `Hallo! Ik wil graag een projectprijs aanvragen voor ${productName} met een hoogte van ${variant.size.toLowerCase()}, leverbaar als ${variant.type.toLowerCase()}. Kunnen jullie mij hier meer informatie over geven?`;
    return `https://wa.me/${phoneNumber}?text=${encodeURIComponent(message)}`;
  };

  const increaseQuantity = () => {
    const currentQty = parseInt(inputValue) || quantity;
    const newQty = Math.min(999, currentQty + 1);
    setQuantity(newQty);
    setInputValue(newQty.toString());
  };

  const decreaseQuantity = () => {
    const currentQty = parseInt(inputValue) || quantity;
    const minQty = variant.minimum_order_quantity || 25;
    const newQty = Math.max(minQty, currentQty - 1);
    setQuantity(newQty);
    setInputValue(newQty.toString());
  };

  const handleInputBlur = () => {
    const value = parseInt(inputValue);
    const minQty = variant.minimum_order_quantity || 25;
    if (isNaN(value) || value < minQty) {
      setInputValue(minQty.toString());
      setQuantity(minQty);
    } else {
      const validValue = Math.min(999, value);
      setInputValue(validValue.toString());
      setQuantity(validValue);
    }
  };

  const typeIcon = variant.type === 'Kale wortel' ? 'kale-wortel.webp' : variant.type === 'Kluit' ? 'met-kluit.webp' : 'in-pot.webp';
  const isKaleWortel = variant.type === 'Kale wortel';

  // Price tier pills for desktop
  const tierPills: { label: string; price: number | null }[] = isKaleWortel
    ? [
        { label: '100+', price: basePriceTier1 },
        { label: '200+', price: priceTier2 },
        { label: '300+', price: priceTier3 },
        { label: '500+', price: priceTier4 },
      ]
    : [
        { label: '25+', price: basePriceTier1 },
        { label: '51+', price: priceTier2 },
        { label: '76+', price: priceTier3 },
        { label: '101+', price: priceTier4 },
      ];

  const validTierPrices = tierPills.map(t => t.price).filter((p): p is number => p != null && p > 0);
  const cheapestTierPrice = validTierPrices.length > 0 ? Math.min(...validTierPrices) : basePriceTier1;
  const cheapestTierIndex = tierPills.findIndex(t => t.price === cheapestTierPrice);
  const firstTierLabel = isKaleWortel ? '100-199 stuks' : '25-50 stuks';
  const firstTierPrice = basePriceTier1;

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={() => navigate(variantUrl)}
      className="group cursor-pointer transition-all duration-300"
    >
      {/* === MOBILE LAYOUT === */}
      <div className="relative flex items-stretch gap-3 rounded-xl bg-white p-3 shadow-sm ring-1 ring-[#e7e1d6] lg:hidden">
        {/* Image - fixed height to prevent hover glitches */}
        <div className="relative h-20 w-20 flex-shrink-0 overflow-hidden rounded-lg">
          <AnimatePresence initial={false}>
            {displayImage ? (
              <motion.img
                key={displayImage}
                src={displayImage}
                alt={`${productName} - ${variant.size}`}
                className="absolute inset-0 h-full w-full object-cover"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2 }}
              />
            ) : (
              <div className="flex h-full w-full items-center justify-center bg-[#e7e1d6]">
                <Package className="h-8 w-8 text-[#cfcac2]" />
              </div>
            )}
          </AnimatePresence>
        </div>

        {/* Info + actions */}
        <div className="flex min-w-0 flex-1 flex-col justify-between gap-1 pr-5">
          {/* Top: title + badges */}
          <div>
            <h3 className="truncate text-sm font-bold leading-tight text-jv-primary group-hover:underline">
              {productName} {variant.size}
            </h3>
            <div className="mt-1 flex flex-wrap items-center gap-1.5">
              <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-medium ${isKaleWortel ? 'bg-amber-800 text-white' : 'bg-transparent text-black ring-1 ring-[#1e2b22]'}`}>
                <img src={`/assets/${typeIcon}`} alt="" className="h-3 w-3 object-contain" />
                {variant.type}
              </span>
              {isInStock && (
                <span className="inline-flex items-center gap-1 rounded-full bg-green-50 px-2 py-0.5 text-[10px] font-medium text-green-700 ring-1 ring-green-200">
                  Op voorraad
                </span>
              )}
            </div>
          </div>

          {/* Bottom: quantity on left, price+button stacked on right */}
          <div className="flex items-end justify-between gap-2" onClick={(e) => e.stopPropagation()}>
            {/* Left: quantity */}
            <div>
              {isInStock && categorySlug !== 'bomen' && !isNotYetAvailable && (
                <div className="flex items-center gap-1">
                  <div className="flex items-center overflow-hidden rounded-full border border-gray-300">
                    <button onClick={decreaseQuantity} className="p-1 hover:bg-gray-100" aria-label="Verminder">
                      <Minus className="h-3 w-3 text-gray-600" />
                    </button>
                    <input
                      type="number"
                      value={inputValue}
                      onChange={(e) => setInputValue(e.target.value)}
                      onBlur={handleInputBlur}
                      className="w-8 border-none bg-transparent py-0.5 text-center text-xs font-bold outline-none [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                      min={variant.minimum_order_quantity || 25}
                      max="999"
                    />
                    <button onClick={increaseQuantity} className="p-1 hover:bg-gray-100" aria-label="Verhoog">
                      <Plus className="h-3 w-3 text-gray-600" />
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Right: cheapest price above button, "Elders goedkoper?" below */}
            <div className="flex flex-col items-end gap-0.5">
              {cheapestTierPrice > 0 && (
                <p className="text-[9px] font-bold uppercase tracking-widest text-jv-primary/50 leading-none">Vanaf</p>
              )}
              <p className="text-base font-black leading-none text-jv-primary">
                {cheapestTierPrice > 0 ? `€${cheapestTierPrice.toFixed(2)}` : 'Prijs op aanvraag'}
              </p>
              {categorySlug === 'bomen' ? (
                <Button asChild className="h-7 rounded-full bg-jv-accent px-3 text-[10px] font-bold text-white" onClick={(e) => e.stopPropagation()}>
                  <a href={getWhatsAppLink()} target="_blank" rel="noopener noreferrer">
                    <ShoppingCart className="mr-1 h-3 w-3" /> Info
                  </a>
                </Button>
              ) : isNotYetAvailable ? (
                <span className="inline-flex h-7 items-center rounded-full border border-jv-primary bg-gray-100 px-3 text-[10px] font-bold text-jv-primary">
                  <CalendarClock className="mr-1 h-3 w-3" />
                  {formatDutchDate(variant.available_from)}
                </span>
              ) : isInStock ? (
                <Button
                  onClick={handleAddToCart}
                  disabled={isAdding}
                  className="h-7 rounded-full bg-jv-accent px-3 text-[10px] font-bold text-white hover:bg-jv-accent/90"
                >
                  <ShoppingCart className="mr-1 h-3 w-3" />
                  {isAdding ? '...' : 'Toevoegen'}
                </Button>
              ) : (
                <Button asChild className="h-7 rounded-full bg-jv-accent px-3 text-[10px] font-bold text-white" onClick={(e) => e.stopPropagation()}>
                  <a href={getWhatsAppLink()} target="_blank" rel="noopener noreferrer">
                    <ShoppingCart className="mr-1 h-3 w-3" /> Offerte
                  </a>
                </Button>
              )}
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); setPriceMatchOpen(true); }}
                className="mt-0.5 text-[10px] font-semibold text-jv-primary/60 underline hover:text-jv-primary"
              >
                Eldens goedkoper?
              </button>
            </div>
          </div>
        </div>

        {/* Favorite */}
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            toggleFavorite({
              id: favoriteId,
              product_variant_id: variant.id,
              product_name: productName,
              variant_size: variant.size,
              image_url: displayImage,
              slug: variant.slug,
              unit_price: getCurrentPrice(quantity),
            });
          }}
          className="absolute right-2 top-2 z-10 flex h-6 w-6 items-center justify-center rounded-full bg-white/90 shadow-sm transition-all hover:scale-110"
          aria-label={isFavorite(favoriteId) ? 'Uit favorieten verwijderen' : 'Aan favorieten toevoegen'}
        >
          <Heart className={`h-3.5 w-3.5 transition-colors ${isFavorite(favoriteId) ? 'text-jv-accent' : 'text-gray-400'}`} fill={isFavorite(favoriteId) ? 'currentColor' : 'none'} />
        </button>
      </div>

      {/* === DESKTOP LAYOUT === */}
      <div className="hidden lg:flex lg:items-stretch lg:gap-6 lg:rounded-xl lg:bg-white lg:p-6 lg:shadow-sm lg:ring-1 lg:ring-[#e7e1d6]">
        {/* Favorite */}
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            toggleFavorite({
              id: favoriteId,
              product_variant_id: variant.id,
              product_name: productName,
              variant_size: variant.size,
              image_url: displayImage,
              slug: variant.slug,
              unit_price: getCurrentPrice(quantity),
            });
          }}
          className="absolute right-6 top-6 z-10 flex h-9 w-9 items-center justify-center rounded-full bg-white/90 shadow-md transition-all hover:scale-110"
          aria-label={isFavorite(favoriteId) ? 'Uit favorieten verwijderen' : 'Aan favorieten toevoegen'}
        >
          <Heart className={`h-5 w-5 transition-colors ${isFavorite(favoriteId) ? 'text-jv-accent' : 'text-gray-400'}`} fill={isFavorite(favoriteId) ? 'currentColor' : 'none'} />
        </button>

        {/* Image */}
        <div className="relative h-48 w-48 flex-shrink-0 overflow-hidden rounded-lg">
          <AnimatePresence initial={false}>
            {displayImage ? (
              <motion.img
                key={displayImage}
                src={displayImage}
                alt={`${productName} - ${variant.size}`}
                className="absolute inset-0 h-full w-full object-cover"
                style={{ borderTopRightRadius: '60px', borderBottomLeftRadius: '60px' }}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2 }}
              />
            ) : (
              <div className="flex h-full w-full items-center justify-center bg-[#e7e1d6]" style={{ borderTopRightRadius: '60px', borderBottomLeftRadius: '60px' }}>
                <Package className="h-12 w-12 text-[#cfcac2]" />
              </div>
            )}
          </AnimatePresence>
        </div>

        {/* Info + Price Tiers */}
        <div className="flex flex-1 flex-col gap-3 pr-8">
          <div>
            <h3 className="text-2xl font-bold text-jv-primary group-hover:underline group-hover:decoration-jv-secondary group-hover:decoration-2 group-hover:underline-offset-4">
              {productName} <span className="text-jv-primary">{variant.size}</span>
            </h3>
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium ${isKaleWortel ? 'bg-amber-800 text-white' : 'bg-transparent text-black ring-1 ring-[#1e2b22]'}`}>
                <img src={`/assets/${typeIcon}`} alt="" className="h-4 w-4 object-contain" />
                {variant.type}
              </span>
              {isInStock && (
                <span className="inline-flex items-center gap-1 rounded-full bg-green-50 px-2.5 py-1 text-xs font-medium text-green-700 ring-1 ring-green-200">
                  Op voorraad
                </span>
              )}
            </div>
          </div>
          <p className="text-sm leading-relaxed text-gray-600">
            Hoogwaardige {productName} met een hoogte van {variant.size.toLowerCase()}. Leverbaar als {variant.type.toLowerCase()} voor optimale aangroei.
          </p>

          {/* Price tier pills */}
          {hasGraduatedPricing && (
            <div className="mt-1 flex flex-wrap gap-2">
              {tierPills.map((tier, i) => (
                tier.price != null && tier.price > 0 ? (
                  <span
                    key={i}
                    className={`inline-flex items-center gap-1 rounded-lg px-2.5 py-1 text-xs font-semibold ${
                      i === cheapestTierIndex
                        ? 'bg-green-50 text-green-700 ring-1 ring-green-300'
                        : 'bg-gray-50 text-gray-600 ring-1 ring-gray-200'
                    }`}
                  >
                    {tier.label}
                    <span className={i === cheapestTierIndex ? 'text-green-700' : 'text-jv-primary'}>€{tier.price.toFixed(2)}</span>
                  </span>
                ) : null
              ))}
            </div>
          )}
          {hasGraduatedPricing && (
            <p className="text-xs text-jv-primary/60">Hoe meer je bestelt, hoe lager de stukprijs!</p>
          )}
        </div>

        {/* Price + Quantity + Cart */}
        <div className="flex w-[260px] flex-shrink-0 flex-col items-center justify-center gap-4 border-l border-[#e7e1d6] pl-6" onClick={(e) => e.stopPropagation()}>
          <div className="text-center">
            <p className="text-[10px] font-bold uppercase tracking-widest text-jv-primary/50">Vanaf</p>
            <p className="text-4xl font-black leading-none text-jv-primary">{cheapestTierPrice > 0 ? `€${cheapestTierPrice.toFixed(2)}` : 'Prijs op aanvraag'}</p>
            <p className="mt-1 text-xs text-jv-dark/40">per stuk incl. BTW</p>
          </div>

          {hasGraduatedPricing && firstTierPrice > 0 && (
            <div className="border-t border-jv-secondary/40 pt-2 text-center">
              <p className="text-xs text-jv-primary/60">
                {firstTierLabel}: <span className="font-bold text-jv-primary">€{firstTierPrice.toFixed(2)}</span>
              </p>
            </div>
          )}

          {isInStock && categorySlug !== 'bomen' && !isNotYetAvailable && (
            <div className="flex items-center gap-2">
              <span className="text-sm font-semibold text-gray-700">Aantal:</span>
              <div className="flex items-center overflow-hidden rounded-full border-2 border-gray-300">
                <button onClick={decreaseQuantity} className="p-2 hover:bg-gray-100" aria-label="Verminder">
                  <Minus className="h-4 w-4 text-gray-600" />
                </button>
                <input
                  type="number"
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  onBlur={handleInputBlur}
                  className="w-12 border-none bg-transparent px-2 py-2 text-center text-base font-bold outline-none [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                  min={variant.minimum_order_quantity || 25}
                  max="999"
                />
                <button onClick={increaseQuantity} className="p-2 hover:bg-gray-100" aria-label="Verhoog">
                  <Plus className="h-4 w-4 text-gray-600" />
                </button>
              </div>
            </div>
          )}

          {categorySlug === 'bomen' ? (
            <Button asChild className="w-full bg-jv-accent font-bold text-white shadow-lg transition-all hover:bg-jv-accent/90 hover:shadow-xl" style={{ borderTopLeftRadius: '12px', borderBottomRightRadius: '12px' }}>
              <a href={getWhatsAppLink()} target="_blank" rel="noopener noreferrer">
                <ShoppingCart className="mr-2 h-5 w-5" />
                Projectprijs aanvragen
              </a>
            </Button>
          ) : isNotYetAvailable ? (
            <Button className="w-full pointer-events-none border-2 border-jv-primary bg-gray-100 font-bold text-jv-primary" style={{ borderTopLeftRadius: '12px', borderBottomRightRadius: '12px' }}>
              <CalendarClock className="mr-2 h-5 w-5" />
              Leverbaar vanaf: {formatDutchDate(variant.available_from)}
            </Button>
          ) : isInStock ? (
            <Button
              onClick={handleAddToCart}
              disabled={isAdding}
              className="w-full bg-jv-accent py-5 font-bold text-white shadow-lg transition-all hover:bg-jv-accent/90 hover:shadow-xl"
              style={{ borderTopLeftRadius: '12px', borderBottomRightRadius: '12px' }}
            >
              <ShoppingCart className="mr-2 h-5 w-5" />
              {isAdding ? 'Bezig...' : 'Toevoegen'}
            </Button>
          ) : (
            <Button asChild className="w-full bg-jv-accent py-5 font-bold text-white shadow-lg transition-all hover:bg-jv-accent/90 hover:shadow-xl" style={{ borderTopLeftRadius: '12px', borderBottomRightRadius: '12px' }}>
              <a href={getWhatsAppLink()} target="_blank" rel="noopener noreferrer">
                <ShoppingCart className="mr-2 h-5 w-5" />
                Vraag offerte aan!
              </a>
            </Button>
          )}

          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setPriceMatchOpen(true); }}
            className="flex items-center justify-center gap-1.5 text-xs font-semibold text-jv-primary/60 underline transition-colors hover:text-jv-primary"
          >
            <Tag className="h-3.5 w-3.5" />
            Eldens goedkoper?
          </button>
        </div>
      </div>

      <PriceMatchModal
        open={priceMatchOpen}
        onOpenChange={setPriceMatchOpen}
        productName={productName}
        variantSize={variant.size}
        variantType={variant.type}
        variantId={variant.id}
        ourPrice={currentPrice}
      />
    </motion.div>
  );
}
